import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Warranty, WarrantyDocument } from '@wsphere/warranties/domain';
import { Subscription } from 'rxjs';
import { WarrantyCustomerPortalService } from '../../../services/warranties.service';

@Component({
  selector: 'ws-warranty-document-repository',
  templateUrl: './warranty-document-repository.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WarrantyDocumentRepositoryComponent implements OnDestroy {
  private currentWarranty?: Warranty.Model;
  private requestId = 0;
  private contextId = 0;
  private previewRequestId = 0;
  private pendingPreviewTab: Window | null = null;

  @ViewChild('removeDialog') removeDialog?: DialogComponent;

  @Input() set warranty(value: Warranty.Model | undefined) {
    const previousId = this.currentWarranty?.id;
    this.currentWarranty = value;
    if (previousId === value?.id) return;

    this.contextId++;
    this.clearActions();
    // Filters belong to one warranty; reset silently so only one request is made.
    this.filters.reset(undefined, { emitEvent: false });
    this.page = 1;
    void this.loadDocuments();
  }

  get warranty(): Warranty.Model | undefined {
    return this.currentWarranty;
  }

  readonly categoryLabels = WarrantyDocument.CATEGORY_LABELS;
  readonly categories = Object.values(WarrantyDocument.Category);
  readonly pageSize = 10;
  readonly filters = new FormGroup({
    category: new FormControl<WarrantyDocument.Category | ''>('', { nonNullable: true }),
    dateFrom: new FormControl('', { nonNullable: true }),
    dateTo: new FormControl('', { nonNullable: true }),
    assetId: new FormControl('', { nonNullable: true }),
  });
  private readonly filterChanges: Subscription;
  documents: WarrantyDocument.DocumentResponse[] = [];
  page = 1;
  totalPages = 0;
  loading = false;
  error = false;
  previewingId: string | null = null;
  downloadingId: string | null = null;
  removalTarget: WarrantyDocument.DocumentResponse | null = null;
  removing = false;
  removalError = false;

  get canUpload(): boolean {
    return this.warranty?.status === Warranty.Status.ACTIVATED;
  }

  /** Only filters that are actually set. */
  get activeFilters(): WarrantyDocument.ListFilters {
    const { category, dateFrom, dateTo, assetId } = this.filters.getRawValue();
    return {
      ...(category ? { category } : {}),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      ...(assetId ? { assetId } : {}),
    };
  }

  get hasFilters(): boolean {
    return Object.keys(this.activeFilters).length > 0;
  }

  get invalidDateRange(): boolean {
    const { dateFrom, dateTo } = this.activeFilters;
    return !!dateFrom && !!dateTo && dateFrom > dateTo;
  }

  constructor(
    private warrantySvc: WarrantyCustomerPortalService,
    private toastSvc: ToastService,
    private changeDetector: ChangeDetectorRef,
    @Inject(DOCUMENT) private browserDocument: Document
  ) {
    this.filterChanges = this.filters.valueChanges.subscribe(() => {
      this.page = 1;
      void this.loadDocuments();
    });
  }

  clearFilters(): void {
    this.filters.reset();
  }

  async loadDocuments(): Promise<void> {
    // Ignore responses superseded by a page/filter/warranty change or component destruction.
    const requestId = ++this.requestId;
    const warrantyId = this.warranty?.id;
    this.documents = [];
    this.totalPages = 0;
    this.error = false;
    this.loading = !!warrantyId && !this.invalidDateRange;
    if (!warrantyId || this.invalidDateRange) return;

    try {
      const response = await this.warrantySvc.listWarrantyDocuments(warrantyId, {
        page: this.page,
        limit: this.pageSize,
        ...this.activeFilters,
      });
      if (requestId !== this.requestId) return;
      // Another tab can remove documents too; recover if the requested page no longer exists.
      const lastPage = Math.max(1, response.meta.totalPages ?? 1);
      if (this.page > lastPage) {
        this.page = lastPage;
        await this.loadDocuments();
        return;
      }
      this.documents = response.items;
      this.totalPages = response.meta.totalPages ?? 1;
    } catch {
      if (requestId !== this.requestId) return;
      this.error = true;
    } finally {
      if (requestId === this.requestId) {
        this.loading = false;
        this.changeDetector.markForCheck();
      }
    }
  }

  changePage(page: number): void {
    if (this.loading || this.removing || page < 1 || page > this.totalPages || page === this.page) return;
    this.page = page;
    void this.loadDocuments();
  }

  trackById(_: number, document: WarrantyDocument.DocumentResponse): string {
    return document.id;
  }

  onUploaded(warrantyId: string): void {
    if (this.warranty?.id !== warrantyId) return;
    this.page = 1;
    void this.loadDocuments();
  }

  async openPreview(document: WarrantyDocument.DocumentResponse): Promise<void> {
    if (this.previewingId || document.warrantyId !== this.warranty?.id) return;
    // Open during the click so browsers do not block the tab after the asynchronous URL request.
    const tab = this.browserDocument.defaultView?.open('about:blank', '_blank');
    if (!tab) {
      this.toastSvc.show({
        type: 'warning',
        title: 'Preview blocked',
        text: 'Allow pop-ups for this portal, then try Preview again.',
      });
      return;
    }
    tab.opener = null;
    tab.document.title = 'Document preview';
    tab.document.body.textContent = 'Loading document…';
    this.pendingPreviewTab = tab;
    this.previewingId = document.id;
    const requestId = this.previewRequestId;
    try {
      const { url } = await this.warrantySvc.getWarrantyDocumentDownloadUrl(document.warrantyId, document.id);
      if (requestId !== this.previewRequestId || tab.closed) return;
      // Navigate rather than fetch: reading the file cross-origin would need CORS on the tenant's bucket.
      // Uploads are limited to PDF and image types, which browsers display inline.
      tab.location.replace(url);
    } catch {
      if (requestId !== this.previewRequestId || tab.closed) return;
      tab.close();
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to preview document',
        text: 'The preview could not be prepared. Try again or use Download.',
      });
    } finally {
      if (requestId === this.previewRequestId) {
        this.pendingPreviewTab = null;
        this.previewingId = null;
        this.changeDetector.markForCheck();
      }
    }
  }

  clearPreview(): void {
    this.previewRequestId++;
    this.pendingPreviewTab?.close();
    this.pendingPreviewTab = null;
    this.previewingId = null;
  }

  async downloadDocument(document: WarrantyDocument.DocumentResponse): Promise<void> {
    if (this.downloadingId || document.warrantyId !== this.warranty?.id) return;
    const contextId = this.contextId;
    this.downloadingId = document.id;
    try {
      const { url } = await this.warrantySvc.getWarrantyDocumentDownloadUrl(
        document.warrantyId,
        document.id,
        'attachment'
      );
      if (contextId !== this.contextId) return;
      // The URL is signed to be saved under the document's name, so following it downloads the file
      // without leaving the portal or reading it cross-origin (which would need bucket CORS).
      const link = this.browserDocument.createElement('a');
      link.href = url;
      this.browserDocument.body.appendChild(link);
      try {
        link.click();
      } finally {
        link.remove();
      }
    } catch {
      if (contextId !== this.contextId) return;
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to download document',
        text: 'The download could not be prepared. Please try again.',
      });
    } finally {
      if (contextId === this.contextId) {
        this.downloadingId = null;
        this.changeDetector.markForCheck();
      }
    }
  }

  confirmRemoval(document: WarrantyDocument.DocumentResponse): void {
    if (this.removing || document.warrantyId !== this.warranty?.id) return;
    this.removalTarget = document;
    this.removalError = false;
    this.removeDialog?.open();
  }

  cancelRemoval(): void {
    this.removalTarget = null;
    this.removalError = false;
  }

  async removeDocument(): Promise<void> {
    const document = this.removalTarget;
    if (!document || this.removing || document.warrantyId !== this.warranty?.id) return;
    const contextId = this.contextId;
    this.removing = true;
    this.removalError = false;
    try {
      await this.warrantySvc.removeWarrantyDocument(document.warrantyId, document.id);
      if (contextId !== this.contextId) return;
      this.removeDialog?.close();
      this.removalTarget = null;
      this.clearPreview();
      this.toastSvc.show({
        type: 'success',
        title: 'Document removed',
        text: 'The document was removed from this warranty’s repository. Claim evidence has been retained.',
      });
      if (this.documents.length === 1 && this.page > 1) this.page--;
      await this.loadDocuments();
    } catch {
      if (contextId !== this.contextId) return;
      this.removalError = true;
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to remove document',
        text: 'The document could not be removed. Please try again.',
      });
    } finally {
      if (contextId === this.contextId) {
        this.removing = false;
        this.changeDetector.markForCheck();
      }
    }
  }

  private clearActions(): void {
    this.clearPreview();
    this.downloadingId = null;
    this.removeDialog?.close();
    this.cancelRemoval();
    this.removing = false;
  }

  ngOnDestroy(): void {
    this.filterChanges.unsubscribe();
    this.requestId++;
    this.contextId++;
    this.clearPreview();
  }
}
